import React, { useState, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactToPrint from 'react-to-print';
import { MeasureImages } from './MeasureImages';

interface FileItem {
    id?: number;
    sketchName?: string;
    sketchWithUrl?: string;
    pictureName?: string;
    pictureWithUrl?: string;
    measureName?: string;
    measureWithUrl?: string;
    videoName?: string;
    videoWithUrl?: string;
}

interface MeasureFileManagerProps {
    existingFiles: FileItem[];
    // Videos travel alongside the existing sketch/photo/pdf channels. The callback signature
    // grew by one slot rather than being refactored into a single object so the existing call
    // sites stay positional-but-explicit and don't accidentally collapse the channels.
    onFilesChange: (sketches: File[], photos: File[], pdfs: File[], videos: File[]) => void;
    onDeleteFile: (fileId: number) => void;
    onBulkDelete?: (ids: number[]) => void;
    sketches: File[];
    photos: File[];
    pdfs: File[];
    videos: File[];
    loading?: boolean;
}

interface FlatFile {
    id: number;
    url: string;
    name: string;
    type: 'photo' | 'sketch' | 'pdf' | 'video';
}

const MeasureFileManager: React.FC<MeasureFileManagerProps> = ({
    existingFiles, onFilesChange, onDeleteFile, onBulkDelete, sketches, photos, pdfs, videos, loading: externalLoading
}) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });

    const [selectMode, setSelectMode] = useState(false);
    const [selected, setSelected] = useState<number[]>([]);
    const [viewImage, setViewImage] = useState<string | null>(null);
    const cameraRef = useRef<HTMLInputElement>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const printRef = useRef<any>(null);

    // Flatten existing files
    const allExisting: FlatFile[] = existingFiles.map(f => {
        if (f.pictureWithUrl) return { id: f.id!, url: f.pictureWithUrl, name: f.pictureName!, type: 'photo' as const };
        if (f.sketchWithUrl) return { id: f.id!, url: f.sketchWithUrl, name: f.sketchName!, type: 'sketch' as const };
        if (f.measureWithUrl) return { id: f.id!, url: f.measureWithUrl, name: f.measureName!, type: 'pdf' as const };
        if (f.videoWithUrl) return { id: f.id!, url: f.videoWithUrl, name: f.videoName!, type: 'video' as const };
        return null;
    }).filter(Boolean) as FlatFile[];

    const allNew = [
        ...photos.map((f, i) => ({ file: f, index: i, type: 'photo' as const })),
        ...sketches.map((f, i) => ({ file: f, index: i, type: 'sketch' as const })),
        ...pdfs.map((f, i) => ({ file: f, index: i, type: 'pdf' as const })),
        ...videos.map((f, i) => ({ file: f, index: i, type: 'video' as const })),
    ];

    const totalCount = allExisting.length + allNew.length;

    // Images for print view (only existing images, not PDFs)
    const printImages = allExisting
        .filter(f => f.type === 'photo' || f.type === 'sketch')
        .map(f => ({ fileUrl: f.url }));

    const handleCameraCapture = () => cameraRef.current?.click();
    const handleFileUpload = () => fileRef.current?.click();

    const handleCameraChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        // Defensive: filter out anything that isn't an image. The input's accept="image/*"
        // and capture="environment" both hint at photos, but desktop browsers can fall through
        // to "All files" and let a video slip in. Previously that video would be appended to
        // `photos` and round-trip as a broken image URL.
        const imageFiles = files.filter(f => f.type.startsWith('image/'));
        if (imageFiles.length > 0) onFilesChange(sketches, [...photos, ...imageFiles], pdfs, videos);
        e.target.value = '';
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        const newPhotos: File[] = [];
        const newPdfs: File[] = [];
        const newVideos: File[] = [];
        files.forEach(f => {
            if (f.type === 'application/pdf') newPdfs.push(f);
            else if (f.type.startsWith('image/')) newPhotos.push(f);
            else if (f.type.startsWith('video/')) newVideos.push(f);
            // Anything else is silently dropped — the accept filter should prevent it from
            // reaching this branch but we keep the guard for robustness against the OS picker
            // returning files with empty `type` (rare with weird extensions).
        });
        onFilesChange(sketches, [...photos, ...newPhotos], [...pdfs, ...newPdfs], [...videos, ...newVideos]);
        e.target.value = '';
    };

    const removeNewFile = (type: string, index: number) => {
        if (type === 'photo') onFilesChange(sketches, photos.filter((_, i) => i !== index), pdfs, videos);
        else if (type === 'sketch') onFilesChange(sketches.filter((_, i) => i !== index), photos, pdfs, videos);
        else if (type === 'video') onFilesChange(sketches, photos, pdfs, videos.filter((_, i) => i !== index));
        else onFilesChange(sketches, photos, pdfs.filter((_, i) => i !== index), videos);
    };

    // Selection
    const toggleSelect = (id: number) => {
        setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const toggleAll = () => {
        if (selected.length === allExisting.length) setSelected([]);
        else setSelected(allExisting.map(f => f.id));
    };

    const deleteSelected = () => {
        if (onBulkDelete) {
            onBulkDelete(selected);
        } else {
            selected.forEach(id => onDeleteFile(id));
        }
        setSelected([]);
        setSelectMode(false);
    };

    const cancelSelect = () => {
        setSelected([]);
        setSelectMode(false);
    };

    const allSelected = allExisting.length > 0 && selected.length === allExisting.length;

    return (
        <div className={`mfm-container ${externalLoading ? 'mfm-busy' : ''}`} style={{ position: 'relative' }}>
            {externalLoading && (
                <div className="mfm-busy-overlay">
                    <div className="mfm-busy-spinner" />
                </div>
            )}
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleCameraChange} style={{ display: 'none' }} />
            <input ref={fileRef} type="file" accept="image/*,.pdf,video/*" multiple onChange={handleFileChange} style={{ display: 'none' }} />

            {/* Top bar */}
            <div className="mfm-bar">
                {selectMode ? (
                    <>
                        <div className="mfm-select-controls">
                            <button className="mfm-select-all" onClick={toggleAll} type="button">
                                <div className={`mfm-checkbox ${allSelected ? 'mfm-checkbox-checked' : ''}`}>
                                    {allSelected && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
                                </div>
                                <FormattedMessage id="measure.files.selectAll" defaultMessage="All" />
                            </button>
                            <span className="mfm-select-count">
                                {selected.length} <FormattedMessage id="measure.files.selected" defaultMessage="selected" />
                            </span>
                        </div>
                        <div className="mfm-bar-actions">
                            <button className="mfm-btn mfm-btn-danger" onClick={deleteSelected} disabled={selected.length === 0} type="button">
                                <FormattedMessage id="common.file.delete" defaultMessage="Delete" /> ({selected.length})
                            </button>
                            <button className="mfm-btn" onClick={cancelSelect} type="button">
                                <FormattedMessage id="common.confirm.cancel" defaultMessage="Cancel" />
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="mfm-bar-left">
                            <span className="mfm-count">
                                {totalCount} <FormattedMessage id="measure.files.count" defaultMessage="files" />
                            </span>
                            {allExisting.length > 0 && (
                                <button className="mfm-select-btn" onClick={() => setSelectMode(true)} type="button">
                                    <FormattedMessage id="measure.files.select" defaultMessage="Select" />
                                </button>
                            )}
                            {printImages.length > 0 && (
                                <ReactToPrint
                                    trigger={() => (
                                        <button className="mfm-print-btn" type="button">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
                                            </svg>
                                            <FormattedMessage id="measure.files.viewAll" defaultMessage="View all" />
                                        </button>
                                    )}
                                    content={() => printRef.current}
                                />
                            )}
                        </div>
                        <div className="mfm-bar-actions">
                            <button className="mfm-btn mfm-btn-camera" onClick={handleCameraCapture} type="button" title={fm('measure.files.camera', 'Camera')}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/>
                                </svg>
                                <FormattedMessage id="measure.files.camera" defaultMessage="Camera" />
                            </button>
                            <button className="mfm-btn" onClick={handleFileUpload} type="button" title={fm('common.file.upload', 'Upload')}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                                </svg>
                                <FormattedMessage id="common.file.upload" defaultMessage="Upload" />
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Film strip */}
            {(allExisting.length > 0 || allNew.length > 0) && (
                <div className="mfm-strip-wrap">
                    <div className="mfm-strip">
                        {allExisting.map(f => (
                            <div key={f.id} className={`mfm-thumb ${selectMode && selected.includes(f.id) ? 'mfm-thumb-selected' : ''}`} title={f.name}>
                                {selectMode ? (
                                    <div className="mfm-thumb-inner" onClick={() => toggleSelect(f.id)}>
                                        <div className={`mfm-thumb-checkbox ${selected.includes(f.id) ? 'mfm-thumb-checkbox-checked' : ''}`}>
                                            {selected.includes(f.id) && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
                                        </div>
                                        {f.type === 'pdf' ? (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                        ) : f.type === 'video' ? (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.5"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></svg>
                                        ) : (
                                            <img src={f.url} alt={f.name} />
                                        )}
                                    </div>
                                ) : f.type === 'pdf' ? (
                                    <a href={f.url} target="_blank" rel="noopener noreferrer" className="mfm-thumb-inner mfm-thumb-pdf">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                    </a>
                                ) : f.type === 'video' ? (
                                    // Existing videos open in a new tab — the browser plays the file inline via its
                                    // native <video> handling without our needing to ship a custom player.
                                    <a href={f.url} target="_blank" rel="noopener noreferrer" className="mfm-thumb-inner mfm-thumb-video">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.5"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></svg>
                                    </a>
                                ) : (
                                    <div className="mfm-thumb-inner" onClick={() => setViewImage(f.url)}>
                                        <img src={f.url} alt={f.name} />
                                    </div>
                                )}
                                {f.type !== 'photo' && !selectMode && (
                                    <span className="mfm-thumb-type">{f.type === 'sketch' ? 'S' : f.type === 'video' ? 'V' : 'P'}</span>
                                )}
                            </div>
                        ))}

                        {allNew.map((f) => (
                            <div key={`new-${f.type}-${f.index}`} className="mfm-thumb mfm-thumb-new" title={f.file.name}>
                                {f.type === 'pdf' ? (
                                    <div className="mfm-thumb-inner mfm-thumb-pdf">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                    </div>
                                ) : f.type === 'video' ? (
                                    // Newly selected videos show a play badge. Click would need to ObjectURL the File
                                    // and pipe it into a <video> element — skipped here since the file isn't on the
                                    // server yet anyway, so "preview before save" is a nicety not a requirement.
                                    <div className="mfm-thumb-inner mfm-thumb-video">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.5"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></svg>
                                    </div>
                                ) : (
                                    <div className="mfm-thumb-inner" onClick={() => setViewImage(URL.createObjectURL(f.file))}>
                                        <img src={URL.createObjectURL(f.file)} alt={f.file.name} />
                                    </div>
                                )}
                                <button className="mfm-thumb-delete" onClick={() => removeNewFile(f.type, f.index)} type="button">×</button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Hidden print view */}
            <div style={{ display: 'none' }}>
                <MeasureImages images={printImages} ref={printRef} />
            </div>

            {/* Lightbox */}
            {viewImage && (
                <div className="mfm-lightbox" onClick={() => setViewImage(null)}>
                    <img src={viewImage} alt="" onClick={e => e.stopPropagation()} />
                    <button className="mfm-lightbox-close" onClick={() => setViewImage(null)} type="button">×</button>
                </div>
            )}
        </div>
    );
};

export default MeasureFileManager;
